import React from 'react';
import './NavigationDrawer.css';
import {
    Flex,
    Text,
    Button,
    Separator,
    Avatar,
    Card
} from '@radix-ui/themes';
import {
    ExitIcon,
    PersonIcon,
    DashboardIcon,
    GearIcon,
    QuestionMarkIcon,
    StarIcon
} from '@radix-ui/react-icons';
import { Member, FILE_SERVER_URL } from '../services/api';
import { Link } from 'react-router-dom';

interface NavigationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onLogout: () => void;
    selectedStudent: Member | null;
}

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
    isOpen,
    onClose,
    onLogout,
    selectedStudent
}) => {
    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <>
            {/* Overlay */}
            {isOpen && (
                <div className="drawer-overlay" onClick={handleOverlayClick} />
            )}

            {/* Drawer */}
            <div className={`navigation-drawer ${isOpen ? 'open' : ''}`}>
                <Flex direction="column" height="100%" p="4">
                    {/* User Profile Section */}
                    <Flex direction="column" align="center" gap="3" p="4" className="user-profile">
                        <Avatar
                            size="8"
                            src={selectedStudent?.avatar && selectedStudent?.avatar !== 'NoImageFound.png' ? `${FILE_SERVER_URL}/show_files/${selectedStudent.avatar}` : undefined}
                            fallback={selectedStudent?.fullName.charAt(0) || 'S'}
                            className="user-avatar"
                        />
                        <Flex direction="column" align="center" gap="1">
                            <Text size="3" weight="bold" className="user-name">
                                {selectedStudent?.fullName || 'Student'}
                            </Text>
                            <Text size="2" color="gray" className="user-mobile">
                                {selectedStudent?.current_rank_title || 'No Rank'}
                            </Text>
                            <Text size="1" color="gray" className="user-joined">
                                Student ID: {selectedStudent?.memberId || 'N/A'}
                            </Text>
                        </Flex>
                    </Flex>

                    <Separator size="4" />

                    {/* Navigation Menu */}
                    <Flex direction="column" gap="3" style={{ flex: 1 }} className="nav-menu">
                        <Card asChild className="nav-card selected">
                            <Link to="/dashboard" tabIndex={0}>
                                <Flex align="center" justify="center" gap="2">
                                    <DashboardIcon />
                                    <Text size="4" weight="bold">Dashboard</Text>
                                </Flex>
                            </Link>
                        </Card>
                        <Card asChild className="nav-card">
                            <Link to="/rank-details" tabIndex={0}>
                                <Flex align="center" justify="center" gap="2">
                                    <StarIcon />
                                    <Text size="4" weight="medium">Rank Details</Text>
                                </Flex>
                            </Link>
                        </Card>
                        <Card asChild className="nav-card">
                            <Link to="/achievements" tabIndex={0}>
                                <Flex align="center" justify="center" gap="2">
                                    <PersonIcon />
                                    <Text size="4" weight="medium">Achievements</Text>
                                </Flex>
                            </Link>
                        </Card>
                        <Card asChild className="nav-card">
                            <Link to="/attendance" tabIndex={0}>
                                <Flex align="center" justify="center" gap="2">
                                    <DashboardIcon />
                                    <Text size="4" weight="medium">Attendance</Text>
                                </Flex>
                            </Link>
                        </Card>
                        <Card asChild className="nav-card">
                            <Link to="/fees-details" tabIndex={0}>
                                <Flex align="center" justify="center" gap="2">
                                    <GearIcon />
                                    <Text size="4" weight="medium">Fees Details</Text>
                                </Flex>
                            </Link>
                        </Card>
                        <Card asChild className="nav-card">
                            <Link to="/profile" tabIndex={0}>
                                <Flex align="center" justify="center" gap="2">
                                    <PersonIcon />
                                    <Text size="4" weight="medium">Profile</Text>
                                </Flex>
                            </Link>
                        </Card>
                    </Flex>

                    <Separator size="4" />

                    {/* Bottom Actions */}
                    <Flex direction="column" gap="2" className="bottom-actions">
                        <Button
                            variant="ghost"
                            size="3"
                            onClick={onLogout}
                            className="logout-button"
                        >
                            <ExitIcon />
                            <Text>Logout</Text>
                        </Button>
                    </Flex>
                </Flex>
            </div>
        </>
    );
};

export default NavigationDrawer; 